import { apiFetch } from '../../shared/apiClient';

export interface CapacitySettings {
  healthyLow: number;
  healthyHigh: number;
  overloadMax: number;
  overloadPenaltyMax: number;
}

export interface CapacityOverride {
  productId: string;
  productName: string;
  settings: CapacitySettings;
}

export interface EffectiveCapacitySettings {
  settings: CapacitySettings;
  isOverride: boolean;
}

export const CapacitySettingsApi = {
  async getOrgCapacitySettings(): Promise<{ settings: CapacitySettings }> {
    const res = await apiFetch('/api/capacity/settings/org');
    if (!res.ok) throw new Error(`Failed to fetch org capacity settings: ${res.statusText}`);
    return res.json();
  },

  async getCapacityOverrides(): Promise<{ overrides: CapacityOverride[] }> {
    const res = await apiFetch('/api/capacity/settings/overrides');
    if (!res.ok) throw new Error(`Failed to fetch capacity overrides: ${res.statusText}`);
    return res.json();
  },

  async saveOrgCapacitySettings(
    settings: CapacitySettings
  ): Promise<{ settings: CapacitySettings }> {
    const res = await apiFetch('/api/capacity/settings/org', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save org capacity settings: ${res.statusText}`);
    }
    return res.json();
  },

  async saveCapacityOverride(
    productId: string,
    settings: CapacitySettings
  ): Promise<{ settings: CapacitySettings; isOverride: boolean }> {
    const res = await apiFetch(
      `/api/capacity/settings/overrides/${encodeURIComponent(productId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(settings),
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save capacity override: ${res.statusText}`);
    }
    return res.json();
  },

  async deleteCapacityOverride(productId: string): Promise<void> {
    const res = await apiFetch(
      `/api/capacity/settings/overrides/${encodeURIComponent(productId)}`,
      { method: 'DELETE' }
    );
    if (!res.ok) throw new Error(`Failed to reset capacity override: ${res.statusText}`);
  },

  async getEffectiveCapacitySettings(): Promise<EffectiveCapacitySettings> {
    const res = await apiFetch('/api/capacity/settings/effective');
    if (!res.ok) {
      throw new Error(`Failed to fetch effective capacity settings: ${res.statusText}`);
    }
    return res.json();
  },
};
